/**
 * Raportti → Raportin asetukset: what the public report shows. Implementation
 * plans on or off (settings.showImplementationPlans, default on), the report
 * PIN that opens sections marked protected (settings.reportPin, which had no
 * UI before), and the way to the section visibility editor on /raportti.
 */
import React, { useEffect, useState } from 'react';
import { ExternalLink, Save } from 'lucide-react';
import Toggle from '../ui/Toggle';
import useInstanceRecord from '../../hooks/useInstanceRecord';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { SwitchRow, Input, FieldHelp, Actions, Button, StatusLine } from '../InstanceSettings/formPrimitives';
import { SectionLabel } from './reportAdminStyles';

export default function ReportSettingsPanel({ instanceId }) {
  const { t } = useTranslation();
  const { instance, setValue } = useInstanceRecord(instanceId);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const storedPin = instance?.settings?.reportPin || '';

  useEffect(() => { setPin(storedPin); }, [storedPin]);

  const run = async (fn) => {
    setBusy(true);
    setStatus(null);
    try {
      await fn();
      setStatus({ text: t('instanceSettingsModal.successSaved') });
    } catch (err) {
      setStatus({ error: true, text: t('settingsPages.saveFailed', { error: err.message }) });
    } finally {
      setBusy(false);
    }
  };

  if (!instance) return null;

  return (
    <div style={{ marginBottom: 40 }}>
      <SectionLabel>{t('reportSettings.title')}</SectionLabel>
      <SwitchRow>
        <div>
          <strong>{t('reportSettings.implementationPlans')}</strong>
          <small>{t('reportSettings.implementationPlansHelp')}</small>
        </div>
        <Toggle
          on={instance.settings?.showImplementationPlans !== false}
          disabled={busy}
          label={t('reportSettings.implementationPlans')}
          onChange={(next) => run(() => setValue('settings', 'showImplementationPlans', next))}
        />
      </SwitchRow>
      <div style={{ margin: '12px 0' }}>
        <strong style={{ fontSize: 14 }}>{t('reportSettings.reportPin')}</strong>
        <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
          <Input
            value={pin}
            onChange={(e) => setPin(e.target.value.trim())}
            aria-label={t('reportSettings.reportPin')}
            disabled={busy}
            style={{ maxWidth: 220 }}
          />
          <Button type="button" $variant="primary" disabled={busy || pin === storedPin}
            onClick={() => run(() => setValue('settings', 'reportPin', pin || null))}>
            <Save /> {t('instanceSettingsModal.saveChanges')}
          </Button>
        </div>
        <FieldHelp>{t('reportSettings.reportPinHelp')}</FieldHelp>
      </div>
      <FieldHelp>{t('reportSettings.sectionAccess')}</FieldHelp>
      <Actions>
        <Button as="a" href={`/raportti?instance=${instanceId}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <ExternalLink /> {t('reportSettings.openEditor')}
        </Button>
        {status && <StatusLine $error={status.error}>{status.text}</StatusLine>}
      </Actions>
    </div>
  );
}
